import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  Button,
  OutlinedInput,
  InputAdornment,
  IconButton,
  LinearProgress,
  CircularProgress,
  Backdrop,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CartCommonCard from "./CartCommonCard";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import { useNavigate } from "react-router-dom";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { CartService } from "services/CartService";
import { useAuth } from "context/authContext";
import { ContentsService } from "services/ContentsService";

interface CartItem {
  id: string;
  afterCoupon: number;
  quantity: number;
  promoCode?: string;
  // discount?: number;
  isPromoApplied?: boolean;
  error?: string;
  contentData: any;
}

const CartData = () => {
  const { currentUser, isLoggedIn, loading, setLoading, handleUpdateCartItem } =
    useAuth();

  const [data, setData] = useState<CartItem[]>([]);

  const [promoCode, setPromoCode] = useState("");
  const [appliedPromoCodes, setAppliedPromoCodes] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [subtotal, setSubtotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [totalDue, setTotalDue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCartItems = async () => {
      setIsLoading(true);
      try {
        // Fetch cart items
        const cartResponse = await CartService.getCartItems(currentUser.uid);

        if (cartResponse && cartResponse.items.length > 0) {
          // Fetch `createdBy` user data for each item
          const itemsWithUserData = await Promise.all(
            cartResponse.items.map(async (item: any) => {
              if (item.contentData?.createdBy) {
                const userData = await ContentsService.getUserByCreatedBy(
                  item.contentData.createdBy
                );
                return {
                  ...item,
                  contentData: {
                    ...item.contentData,
                    createdBy: userData, // Add resolved user data
                  },
                };
              }
              return item;
            })
          );

          setData(itemsWithUserData as CartItem[]);
        } else {
          console.warn("Cart is empty or invalid");
          setData([]); // Empty cart fallback
        }
      } catch (error) {
        console.error("Error fetching cart items:", error);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser?.uid) {
      fetchCartItems();
    }
  }, [currentUser]);


  useEffect(() => {
    const taxRate = 0.1; // Example tax rate of 10%

    const newSubtotal = data.reduce(
      (acc, item) => acc + item?.contentData?.price * item.quantity,
      0
    );

    const newDiscount = data.reduce((acc, item) => {
      if (item.isPromoApplied && item.afterCoupon < item?.contentData?.price) {
        return (
          acc + (item?.contentData?.price - item.afterCoupon) * item.quantity
        );
      }
      return acc;
    }, 0);

    const newTax = (newSubtotal - newDiscount) * taxRate;
    setSubtotal(newSubtotal);
    setDiscount(newDiscount);
    setTotalDue(newSubtotal - newDiscount + newTax);
  }, [data]);

  const handleDelete = async (id: string) => {
    try {
      // Show a confirmation alert
      const isConfirmed = window.confirm(
        "Are you sure you want to delete this item?"
      );
      if (!isConfirmed) {
        console.log("Delete action canceled");
        return;
      }

      // Call deleteCart to remove the item from Firestore
      await CartService.deleteCart(id.toString());

      // Remove the card from the local state
      setData((prevData) => prevData.filter((item) => item.id !== id));
      console.log(`Card with ID ${id} deleted successfully`);
    } catch (error) {
      console.error("Error deleting card:", error);
    }
  };

  const handleQuantityChange = async (id: string, delta: number) => {
    // setLoading(true);
    setData((prevData) =>
      prevData.map((item) =>
        item.id === id
          ? { ...item, quantity: Math.max(1, item.quantity + delta) }
          : item
      )
    );

    const updatedItem = data.find((item) => item.id === id);
    if (updatedItem) {
      const newQuantity = Math.max(1, updatedItem.quantity + delta);
      try {
        await handleUpdateCartItem(id, newQuantity);
      } catch (error) {
        console.error("Error updating quantity in Firestore:", error);
      }
      finally {
        setLoading(false);
      }
    }
  };

  const handlePromoCodeChange = (id: string, value: string) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.id === id
          ? { ...item, promoCode: value, error: "" } // Clear error on input change
          : item
      )
    );
  };

  const applyPromoCode = (id: string) => {
    setData((prevData) =>
      prevData.map((item) => {
        if (item.id === id) {
          const promo = item.contentData?.promotion;
          const isPromoValid =
            promo?.isActive && promo?.code === item.promoCode;

          if (isPromoValid) {
            const discountPercent = promo.discountPercent || 0;
            const discountAmount =
              (item?.contentData?.price * discountPercent) / 100;

            return {
              ...item,
              afterCoupon: item?.contentData?.price - discountAmount,
              isPromoApplied: true,
              error: "",
            };
          } else {
            return {
              ...item,
              isPromoApplied: false,
              error: "Invalid or inactive promo code",
            };
          }
        }
        return item;
      })
    );
  };

  const removePromoCode = (id: string) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.id === id
          ? {
            ...item,
            promoCode: "",
            isPromoApplied: false,
            afterCoupon: item?.contentData?.price,
            error: "",
          }
          : item
      )
    );
  };

  const handleNavigateCheckOut = () => {
    const serializableData = JSON.parse(JSON.stringify(data));
    navigate("/checkout", { state: { data: serializableData } });
  };

  return (
    <Grid position={"relative"}>
      <Grid
        display={"flex"}
        flexDirection={"column"}
        justifyContent={"center"}
        gap={2}
        p={5}
        px={{ xs: 2, md: 10 }}
      >
        <Box
          pt={{ xs: 10, md: 3 }}
          fontWeight={700}
          fontSize="35px"
          color="#233F91"
          display="flex"
          alignItems="center"
        >
          Cart
        </Box>
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="40vh"
            width="100vw"
          >
            <CircularProgress color="primary" size={60} />
          </Box>
        ) : data.length === 0 ? (
          <Box mt={5} display={"flex"} flexDirection={"column"} gap={2}>
            <Typography
              display="flex"
              alignItems="center"
              gap={1}
              fontSize={22}
              color="#797979"
              fontWeight={400}
            >
              <ErrorOutlineIcon />
              Cart empty
            </Typography>
            <Button
              sx={styleSheet.browseContentBtn}
              onClick={() => navigate("/")}
            >
              Browse Content
            </Button>
          </Box>
        ) : (
          <>
            <Box>
              <CartCommonCard
                data={data}
                onQuantityChange={handleQuantityChange}
                titleColor="#233F91"
                onDelete={handleDelete}
                appliedPromoCodes={appliedPromoCodes || []}
                removePromoCode={removePromoCode}
                handlePromoCodeChange={handlePromoCodeChange}
                applyPromoCode={applyPromoCode}
                error={error}
              />
            </Box>

            <Box
              display={"flex"}
              flexDirection={"column"}
              gap={2}
              mt={{ xs: 0, md: 5 }}
              width={{ xs: "100%", md: "800px" }}
            >
              <Box display={"flex"} gap={2}>
                <Typography color={"#595959"} fontSize={28} fontWeight={500}>
                  Subtotal:
                </Typography>
                <Typography color={"#595959"} fontSize={28} fontWeight={500}>
                  ${subtotal.toFixed(0)}
                </Typography>
              </Box>
              <Box display={"flex"} justifyContent={"space-between"}>
                <Typography sx={styleSheet.textHeading}>Promo Code</Typography>
                <Typography
                  sx={{
                    ...styleSheet.textHeadingPrice,
                    color: "#1DA944",
                  }}
                >
                  -${discount.toFixed(2)}
                </Typography>
              </Box>
              <Box display={"flex"} justifyContent={"space-between"}>
                <Typography sx={styleSheet.textHeading}>Tax</Typography>
                <Typography sx={styleSheet.textHeadingPrice}>
                  {/* ${(subtotal * 0.1).toFixed(2)} */}$
                  {(subtotal - discount) * 0.1}
                </Typography>
              </Box>
              <Box
                display={"flex"}
                sx={{ borderTop: "1px solid #00A79D" }}
              ></Box>
              <Box display={"flex"} justifyContent={"space-between"} mt={2}>
                <Typography sx={styleSheet.textHeading}>Total Due</Typography>
                <Typography sx={styleSheet.textHeadingPrice}>
                  ${totalDue.toFixed(2)}
                </Typography>
              </Box>
            </Box>
            <Button
              sx={styleSheet.checkOutBtn}
              onClick={handleNavigateCheckOut}
            >
              Proceed to Checkout
            </Button>


          </>
        )}

      </Grid>
    </Grid>
  );
};

export default CartData;

const styleSheet = {
  outLineInput: {
    width: "331px",
    height: "65px",
    borderRadius: "15px",
    paddingRight: "4px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#D3D3D3",
    },
  },
  outLineBtn: {
    width: "96px",
    height: "57px",
    borderRadius: "15px",
    bgcolor: "#233F91",
    color: "#fff",
    "&:hover": {
      bgcolor: "#1A73E8",
    },
  },
  appliedCode: {
    border: "1px solid #D3D3D3",
    borderRadius: "15px",
    px: "10px",
    width: "322px",
    height: "60px",
  },
  textHeading: {
    fontWeight: 400,
    fontSize: "20px",
    color: "#313131",
    lineHeight: "24px",
  },
  textHeadingPrice: {
    fontWeight: 700,
    fontSize: "18px",
    color: "#000000",
    lineHeight: "21.6px",
  },
  checkOutBtn: {
    width: "214px",
    marginTop: "15px",
    height: "57px",
    borderRadius: "15px",
    bgcolor: "#233F91",
    fontSize: "18px",
    fontWeight: "700",
    color: "#fff",
    "&:hover": {
      bgcolor: "#1A73E8",
    },
  },
  browseContentBtn: {
    width: "176px",
    height: "57px",
    borderRadius: "15px",
    marginTop: "20px",
    bgcolor: "#233F91",
    fontSize: "18px",
    fontWeight: "700",
    color: "#fff",
    "&:hover": {
      bgcolor: "#1A73E8",
    },
  },
};
